import * as React from 'react';
import { CaretDown, CaretUp, CheckCircle, XCircle } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { brandShadowEffect } from '@/lib/styles';
import { cn } from '@/lib/utils';

export interface SuggestionCardSource {
  /** Eyebrow label, e.g. "1 day ago from" — rendered uppercase by the card. */
  label: string;
  /** Display name of the source, e.g. "team-meetingFEB.mp4". */
  name: string;
  /**
   * Optional icon next to the source name. Pass a `@phosphor-icons/react` icon.
   */
  icon?: React.ReactNode;
}

/**
 * Pagination state for the side paginator column. When omitted, the paginator
 * column (and its divider) is not rendered at all.
 */
export interface SuggestionCardPagination {
  /** 1-based index of this suggestion within the set. */
  currentIndex: number;
  /** Total number of suggestions in the set. */
  totalCount: number;
  /** Fires when the user navigates to the previous suggestion. Omit to hide the button. */
  onPrevious?: () => void;
  /** Fires when the user navigates to the next suggestion. Omit to hide the button. */
  onNext?: () => void;
}

/**
 * Visual diff shown between the source row and the reason. Renders as a
 * single bordered row with the old text struck-through in destructive color
 * and the new text in link color.
 *
 * - For replace ops, set both `oldText` and `newText`.
 * - For delete ops, set only `oldText`.
 * - For insert ops, set only `newText`.
 *
 * When both fields are empty/undefined the diff row is not rendered.
 */
export interface SuggestionCardDiff {
  /** Original text being removed. Destructive. */
  oldText?: string;
  /** Proposed replacement / insertion. Link color. */
  newText?: string;
}

export interface SuggestionCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /**
   * Source metadata shown at the top of the card. Omit to skip the source
   * row entirely — useful when the host doesn't have source info to surface.
   */
  source?: SuggestionCardSource;
  /**
   * Old vs. new text for the suggestion. Renders the bordered diff row
   * between the source and the reason. Omit to skip the diff row entirely.
   */
  diff?: SuggestionCardDiff;
  /**
   * Host-provided explanation for why this suggestion was raised. Renders
   * as the body paragraph below the diff. Omit to skip the reason line.
   */
  reason?: string;

  /**
   * Fires when the user accepts this suggestion. When omitted, the accept
   * button is rendered disabled (so the affordance is visible but inert).
   */
  onAccept?: () => void;
  /**
   * Fires when the user rejects this suggestion. When omitted, the reject
   * button is rendered disabled.
   */
  onReject?: () => void;
  /** Disable the accept button while an accept is in-flight. */
  acceptDisabled?: boolean;
  /** Disable the reject button while a reject is in-flight. */
  rejectDisabled?: boolean;

  /**
   * Paginator state. When omitted, the whole paginator column (and its
   * dividing border) is not rendered.
   */
  pagination?: SuggestionCardPagination;
  readOnly?: boolean;
}

/**
 * Floating card that displays a single review suggestion with its source,
 * accept/reject actions, and an optional paginator for navigating within-article
 * suggestions. Purely presentational — the host wires callbacks to the
 * reviewSuggestions plugin / API layer.
 */
export const SuggestionCard = React.forwardRef<HTMLDivElement, SuggestionCardProps>(function SuggestionCard(
  {
    source,
    diff,
    reason,
    onAccept,
    onReject,
    acceptDisabled,
    rejectDisabled,
    pagination,
    className,
    readOnly,
    ...props
  },
  ref,
) {
  const acceptIsDisabled = acceptDisabled || !onAccept;
  const rejectIsDisabled = rejectDisabled || !onReject;
  const hasDiff = Boolean(diff && diff.oldText && diff.newText);

  return (
    <div
      ref={ref}
      role="group"
      aria-label="Review suggestion"
      className={cn(
        // The card grows with short suggestions, then caps so long bodies scroll.
        '!grid max-h-[calc(var(--knowledge-review-scroll-height)-250px)] w-full grid-rows-[auto_minmax(0,1fr)_auto] items-stretch gap-4 rounded-lg bg-surface p-4 border shadow-md dark:shadow-brand-dark',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-col justify-start gap-1">
        {source && (
          <>
            <span className="text-xs font-bold uppercase leading-4 text-default">{source.label}</span>
            <div className="flex items-center gap-0.5 [&_svg]:size-4 [&_svg]:shrink-0">
              <span className="flex items-center text-subtle" aria-hidden="true">
                {source.icon}
              </span>
              <span className="text-sm leading-4 text-subtle break-all">{source.name}</span>
            </div>
          </>
        )}
      </div>

      <div className="flex min-w-0 min-h-0 items-stretch gap-2">
        {/* min-h-0 lets this flex child shrink below its content size so the ScrollArea actually scrolls. */}
        <ScrollArea className="-mr-2 min-h-0 flex-1 pr-2">
          <div className="flex w-full flex-col">
            <span className="text-xs font-bold uppercase leading-4 text-default">Why</span>
            {reason && <span className="text-sm leading-4 text-default">{reason}</span>}

            {hasDiff && <SuggestionCardDiffRow oldText={diff?.oldText} newText={diff?.newText} />}
          </div>
        </ScrollArea>

        {pagination && pagination.totalCount > 1 && <SuggestionCardPaginator {...pagination} />}
      </div>

      <div className="flex min-w-0 items-end">
        {!readOnly && (
          <div className="flex shrink-0 items-center gap-2">
            {!acceptIsDisabled && (
              <Button
                type="button"
                variant="default"
                elevation="shadow"
                size="icon"
                onClick={onAccept}
                aria-label="Accept suggestion"
                className={cn('rounded-md border-border bg-green-400 text-default', brandShadowEffect)}
              >
                <CheckCircle weight="regular" aria-hidden="true" />
              </Button>
            )}
            {!rejectIsDisabled && (
              <Button
                type="button"
                variant="destructive"
                elevation="shadow"
                size="icon"
                onClick={onReject}
                aria-label="Reject suggestion"
                className={cn('rounded-md border-border bg-red-400 text-default', brandShadowEffect)}
              >
                <XCircle weight="regular" aria-hidden="true" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Bordered old/new diff row. Old text is rendered first in destructive color,
 * new text follows in link color. Either side can be
 * omitted (delete-only or insert-only).
 */
function SuggestionCardDiffRow({ oldText, newText }: SuggestionCardDiff) {
  return (
    <div className="flex flex-col items-baseline gap-2 border-t border-dark-300 py-4 text-sm leading-4 mt-4">
      {oldText && (
        <div className="text-destructive">
          <span className="font-bold">Replace: </span>
          <span>{oldText}</span>
        </div>
      )}
      {newText && (
        <div className="text-blue">
          <span className="font-bold">With: </span>
          <span>{newText}</span>
        </div>
      )}
    </div>
  );
}

function SuggestionCardPaginator({ currentIndex, totalCount, onPrevious, onNext }: SuggestionCardPagination) {
  const hasPrevious = currentIndex > 1 && !!onPrevious;
  const hasNext = currentIndex < totalCount && !!onNext;

  return (
    <div className="flex w-10 shrink-0 flex-col items-center justify-center self-stretch py-2 pl-3">
      <div className="flex flex-col items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onPrevious}
          disabled={!hasPrevious}
          aria-label="Previous suggestion"
          className="text-subtlest disabled:invisible"
        >
          <CaretUp aria-hidden="true" className="size-3.5" />
        </Button>
        <span className="text-xs leading-4 text-subtlest tabular-nums" aria-hidden="true">
          {currentIndex}/{totalCount}
        </span>
        <span className="sr-only" aria-live="polite">
          Suggestion {currentIndex} of {totalCount}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onNext}
          disabled={!hasNext}
          aria-label="Next suggestion"
          className="text-subtlest disabled:invisible"
        >
          <CaretDown aria-hidden="true" className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}
